import { AloftDate, dropNaN } from '@aloft/toolkit-shared';

import { CompSelectionProperty } from '../../types.js';

export const SIMILARITY_FEATURES = [
  'saleDate',
  'gla',
  'lotSize',
  'bathCount',
  'bedroomCount',
  'proximityMiles',
  'yearBuilt',
] as const;

export type SimilarityFeatureKey = (typeof SIMILARITY_FEATURES)[number];

type ScSimilarityFeatureParams = {
  weight: number /** How important is the feature in the similarity calculation */;
  overrideForSubject?: (effectiveDate: AloftDate) => number /** Value override for subject */;
  maxRange: number /** Maximum range for the feature */;
};

type SimilarityFeatureDefs = Record<SimilarityFeatureKey, ScSimilarityFeatureParams>;
export const similarityFeatureDefs: SimilarityFeatureDefs = {
  saleDate: {
    weight: 6,
    overrideForSubject: (effectiveDate) => effectiveDate.toNumericDate(),
    maxRange: 365 /** 1 year in days */,
  },
  lotSize: {
    weight: 12,
    maxRange: 2000 /** 2,000 sqft */,
  },
  bedroomCount: {
    weight: 0,
    maxRange: 4 /** 4 bedrooms */,
  },
  bathCount: {
    weight: 6,
    maxRange: 4 /** 4 bathrooms */,
  },
  gla: {
    weight: 25,
    maxRange: 2500 /** 2,500 sqft */,
  },
  proximityMiles: {
    weight: 45,
    overrideForSubject: () => 0,
    maxRange: 10 /** 10 miles */,
  },
  yearBuilt: {
    weight: 4,
    maxRange: 35 /** 35 years */,
  },
};

type SimilarityFeatureMapping = Record<
  SimilarityFeatureKey,
  (comp: CompSelectionProperty) => number | undefined
>;
export const similarityFeatureMapping: SimilarityFeatureMapping = {
  saleDate: (comp) => comp.saleDate?.toNumericDate(),
  gla: (comp) => dropNaN(comp.gla?.getUnitAmount('sqft') ?? NaN),
  lotSize: (comp) => dropNaN(comp.lotSize?.getUnitAmount('sqft') ?? NaN),
  bathCount: (comp) => (comp?.fullBaths ?? 0) + (comp?.halfBaths ?? 0),
  bedroomCount: (comp) => comp.bedroomCount,
  proximityMiles: (comp) => comp.distanceToSubject,
  yearBuilt: (comp) => comp.yearBuilt,
};
